/**
 * FERP Portal - SubgraphMetadata Error Handler
 *
 * DATA-DRIVEN architecture error handling for collision detection:
 * - Parses GraphQL error messages for collision types
 * - Displays user-friendly notifications using Mantine
 * - Suggests namespace conventions when errors occur
 *
 * FERP Principles:
 * - Life-critical: User must understand WHY operations failed
 * - Data-driven: Error messages include actionable suggestions
 * - Audit trail: All errors are logged with context
 *
 * Collision Types Handled:
 * - Route collision: Two subgraphs define the same route path
 * - NavItem collision: Two subgraphs use the same nav item ID
 * - EntityType collision: Two subgraphs define the same entity type name
 * - PermissionScope collision: Two subgraphs define the same permission scope
 * - Namespace validation: Prefixes don't match subgraph convention
 */

import { notifications } from '@mantine/notifications';
// @ts-expect-error TS2305
import type { ApolloError } from '@apollo/client';

// ============================================================================
// Collision Error Types
// ============================================================================

export type CollisionType =
  | 'RouteCollision'
  | 'NavItemCollision'
  | 'EntityTypeCollision'
  | 'PermissionScopeCollision'
  | 'NamespaceValidation'
  | 'MultipleCollisions'
  | 'ReseedRequired'
  | 'RebuildRequired'
  | 'CodegenStale'
  | 'YamlConfigInvalid'
  | 'ConfigNotFound'
  | 'DatabaseConnectivity'
  | 'Unknown';

export interface ParsedCollisionError {
  type: CollisionType;
  message: string;
  identifier: string; // The conflicting path/id/name/scope
  firstSubgraph?: string;
  secondSubgraph?: string;
  suggestion: string;
  /** Correlation ID for linking to backend logs */
  correlationId?: string;
  /** Error chain for debugging (only in dev/debug mode) */
  errorChain?: string[];
}

// ============================================================================
// Debug Hint Types (from backend DebugHint enum)
// ============================================================================

/**
 * Debug hints from backend for actionable error resolution.
 * These are serialized from Rust's DebugHint enum.
 */
export type DebugHintType =
  | 'YamlConfigInvalid'
  | 'RebuildRequired'
  | 'ReseedRequired'
  | 'CodegenStale'
  | 'NetworkTimeout'
  | 'PermissionDenied'
  | 'ConfigNotFound'
  | 'SubgraphCollision'
  | 'DatabaseConnectivity'
  | 'MissingEnvVar'
  | 'None';

export interface DebugHint {
  type: DebugHintType;
  // YamlConfigInvalid fields
  file?: string;
  line?: number;
  column?: number;
  parse_error?: string;
  // RebuildRequired fields
  reason?: string;
  command?: string;
  // ReseedRequired fields
  config_type?: string;
  // CodegenStale fields
  schema_changed?: boolean;
  // NetworkTimeout fields
  service?: string;
  timeout_secs?: number;
  // PermissionDenied fields
  required_scopes?: string[];
  current_scopes?: string[];
  // ConfigNotFound fields
  config_id?: string;
  expected_filename?: string;
  config_dir?: string;
  // SubgraphCollision fields
  collision_type?: string;
  identifier?: string;
  subgraphs?: string[];
  suggestion?: string;
  // DatabaseConnectivity fields
  database?: string;
  is_local_dev?: boolean;
  check_command?: string;
  // MissingEnvVar fields
  var_name?: string;
  example_value?: string;
}

/**
 * GraphQL error extensions from FERP backend.
 */
export interface FerpErrorExtensions {
  code?: string;
  category?: string;
  http_status?: number;
  retriable?: boolean;
  life_critical?: boolean;
  correlation_id?: string;
  debug_hint?: DebugHint;
  suggestion?: string;
  error_chain?: string[];
}

// ============================================================================
// Namespace Convention Constants
// ============================================================================

const NAMESPACE_CONVENTIONS = {
  routes: {
    pattern: '/{subgraph_id}/',
    example: '/rcm/tasks, /fhir/patients',
    description: 'Routes must start with /{subgraph_id}/ prefix',
  },
  navItems: {
    pattern: '{subgraph_id}-',
    example: 'rcm-tasks, fhir-patients',
    description: 'Nav item IDs must be prefixed with {subgraph_id}-',
  },
  scopes: {
    pattern: '{subgraph_id}:',
    example: 'rcm:tasks:read, fhir:patients:write',
    description: 'Permission scopes must be prefixed with {subgraph_id}:',
  },
  entityTypes: {
    pattern: 'PascalCase unique name',
    example: 'RcmTask (not Task), FhirPatient (not Patient)',
    description: 'Entity type names should be globally unique, consider subgraph prefix',
  },
};

// ============================================================================
// Error Parsing Functions
// ============================================================================

/**
 * Parse a collision error message to extract details.
 *
 * Expected formats from backend:
 * - "Route collision: '/tasks' is defined by both 'rcm' and 'compliance'..."
 * - "Nav item collision: ID 'dashboard' is defined by both 'rcm' and 'compliance'"
 * - "Entity type collision: 'Task' is defined by both 'rcm' and 'compliance'"
 * - "Permission scope collision: 'tasks:read' is defined by both 'rcm' and 'compliance'"
 * - "Namespace validation failed: ..."
 */
function parseCollisionMessage(message: string): ParsedCollisionError {
  const lowercaseMsg = message.toLowerCase();

  // Route collision
  if (lowercaseMsg.includes('route collision') || lowercaseMsg.includes('routecollision')) {
    const pathMatch = message.match(/'([^']+)'/);
    const subgraphMatch = message.match(/both '([^']+)' and '([^']+)'/);

    return {
      type: 'RouteCollision',
      message,
      identifier: pathMatch?.[1] ?? 'unknown',
      firstSubgraph: subgraphMatch?.[1],
      secondSubgraph: subgraphMatch?.[2],
      suggestion: buildRouteSuggestion(pathMatch?.[1], subgraphMatch?.[1], subgraphMatch?.[2]),
    };
  }

  // Nav item collision
  if (lowercaseMsg.includes('nav item collision') || lowercaseMsg.includes('navitemcollision')) {
    const idMatch = message.match(/'([^']+)'/);
    const subgraphMatch = message.match(/both '([^']+)' and '([^']+)'/);

    return {
      type: 'NavItemCollision',
      message,
      identifier: idMatch?.[1] ?? 'unknown',
      firstSubgraph: subgraphMatch?.[1],
      secondSubgraph: subgraphMatch?.[2],
      suggestion: buildNavItemSuggestion(idMatch?.[1], subgraphMatch?.[1], subgraphMatch?.[2]),
    };
  }

  // Entity type collision
  if (
    lowercaseMsg.includes('entity type collision') ||
    lowercaseMsg.includes('entitytypecollision')
  ) {
    const nameMatch = message.match(/'([^']+)'/);
    const subgraphMatch = message.match(/both '([^']+)' and '([^']+)'/);

    return {
      type: 'EntityTypeCollision',
      message,
      identifier: nameMatch?.[1] ?? 'unknown',
      firstSubgraph: subgraphMatch?.[1],
      secondSubgraph: subgraphMatch?.[2],
      suggestion: buildEntityTypeSuggestion(nameMatch?.[1], subgraphMatch?.[1], subgraphMatch?.[2]),
    };
  }

  // Permission scope collision
  if (
    lowercaseMsg.includes('permission scope collision') ||
    lowercaseMsg.includes('permissionscopecollision')
  ) {
    const scopeMatch = message.match(/'([^']+)'/);
    const subgraphMatch = message.match(/both '([^']+)' and '([^']+)'/);

    return {
      type: 'PermissionScopeCollision',
      message,
      identifier: scopeMatch?.[1] ?? 'unknown',
      firstSubgraph: subgraphMatch?.[1],
      secondSubgraph: subgraphMatch?.[2],
      suggestion: buildScopeSuggestion(scopeMatch?.[1], subgraphMatch?.[1], subgraphMatch?.[2]),
    };
  }

  // Namespace validation error
  if (
    lowercaseMsg.includes('namespace validation') ||
    lowercaseMsg.includes('must start with') ||
    lowercaseMsg.includes('must be prefixed')
  ) {
    return {
      type: 'NamespaceValidation',
      message,
      identifier: extractNamespaceIdentifier(message),
      suggestion: buildNamespaceValidationSuggestion(message),
    };
  }

  // Multiple collisions
  if (lowercaseMsg.includes('multiple collision') || lowercaseMsg.includes('multiplecollision')) {
    return {
      type: 'MultipleCollisions',
      message,
      identifier: 'multiple',
      suggestion:
        'Multiple naming conflicts detected. Review each collision below and apply namespace prefixes.',
    };
  }

  // Unknown error type
  return {
    type: 'Unknown',
    message,
    identifier: 'unknown',
    suggestion:
      'An unexpected error occurred. Please review the error details and ensure all subgraph configurations follow namespace conventions.',
  };
}

// ============================================================================
// Suggestion Builder Functions
// ============================================================================

function buildRouteSuggestion(
  path?: string,
  firstSubgraph?: string,
  secondSubgraph?: string
): string {
  if (!path) {
    return `Routes must use subgraph-prefixed paths. Pattern: ${NAMESPACE_CONVENTIONS.routes.pattern}`;
  }

  const suggestions: string[] = [];

  if (firstSubgraph) {
    suggestions.push(`'/${firstSubgraph}${path}' for ${firstSubgraph}`);
  }
  if (secondSubgraph) {
    suggestions.push(`'/${secondSubgraph}${path}' for ${secondSubgraph}`);
  }

  return suggestions.length > 0
    ? `Use subgraph-prefixed paths: ${suggestions.join(' or ')}`
    : `Route '${path}' conflicts. Use pattern: ${NAMESPACE_CONVENTIONS.routes.pattern} (e.g., ${NAMESPACE_CONVENTIONS.routes.example})`;
}

function buildNavItemSuggestion(
  id?: string,
  firstSubgraph?: string,
  secondSubgraph?: string
): string {
  if (!id) {
    return `Nav item IDs must use subgraph prefixes. Pattern: ${NAMESPACE_CONVENTIONS.navItems.pattern}`;
  }

  const suggestions: string[] = [];

  if (firstSubgraph) {
    suggestions.push(`'${firstSubgraph}-${id}' for ${firstSubgraph}`);
  }
  if (secondSubgraph) {
    suggestions.push(`'${secondSubgraph}-${id}' for ${secondSubgraph}`);
  }

  return suggestions.length > 0
    ? `Use subgraph-prefixed IDs: ${suggestions.join(' or ')}`
    : `Nav item ID '${id}' conflicts. Use pattern: ${NAMESPACE_CONVENTIONS.navItems.pattern} (e.g., ${NAMESPACE_CONVENTIONS.navItems.example})`;
}

function buildEntityTypeSuggestion(
  name?: string,
  firstSubgraph?: string,
  secondSubgraph?: string
): string {
  if (!name) {
    return `Entity type names should be globally unique. ${NAMESPACE_CONVENTIONS.entityTypes.description}`;
  }

  const pascalCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  const suggestions: string[] = [];

  if (firstSubgraph) {
    suggestions.push(`'${pascalCase(firstSubgraph)}${name}' for ${firstSubgraph}`);
  }
  if (secondSubgraph) {
    suggestions.push(`'${pascalCase(secondSubgraph)}${name}' for ${secondSubgraph}`);
  }

  return suggestions.length > 0
    ? `Use unique names: ${suggestions.join(' or ')}`
    : `Entity type '${name}' conflicts. Consider prefixing: ${NAMESPACE_CONVENTIONS.entityTypes.example}`;
}

function buildScopeSuggestion(
  scope?: string,
  firstSubgraph?: string,
  secondSubgraph?: string
): string {
  if (!scope) {
    return `Permission scopes must use subgraph prefixes. Pattern: ${NAMESPACE_CONVENTIONS.scopes.pattern}`;
  }

  const suggestions: string[] = [];

  if (firstSubgraph) {
    suggestions.push(`'${firstSubgraph}:${scope}' for ${firstSubgraph}`);
  }
  if (secondSubgraph) {
    suggestions.push(`'${secondSubgraph}:${scope}' for ${secondSubgraph}`);
  }

  return suggestions.length > 0
    ? `Use subgraph-prefixed scopes: ${suggestions.join(' or ')}`
    : `Scope '${scope}' conflicts. Use pattern: ${NAMESPACE_CONVENTIONS.scopes.pattern} (e.g., ${NAMESPACE_CONVENTIONS.scopes.example})`;
}

function extractNamespaceIdentifier(message: string): string {
  // Try to extract the problematic identifier from namespace validation messages
  const match = message.match(/'([^']+)'/);
  return match?.[1] ?? 'unknown';
}

function buildNamespaceValidationSuggestion(message: string): string {
  const lowercaseMsg = message.toLowerCase();

  if (lowercaseMsg.includes('route')) {
    return `${NAMESPACE_CONVENTIONS.routes.description}. Example: ${NAMESPACE_CONVENTIONS.routes.example}`;
  }
  if (lowercaseMsg.includes('nav')) {
    return `${NAMESPACE_CONVENTIONS.navItems.description}. Example: ${NAMESPACE_CONVENTIONS.navItems.example}`;
  }
  if (lowercaseMsg.includes('scope') || lowercaseMsg.includes('permission')) {
    return `${NAMESPACE_CONVENTIONS.scopes.description}. Example: ${NAMESPACE_CONVENTIONS.scopes.example}`;
  }
  if (lowercaseMsg.includes('entity')) {
    return `${NAMESPACE_CONVENTIONS.entityTypes.description}. Example: ${NAMESPACE_CONVENTIONS.entityTypes.example}`;
  }

  return 'Ensure all identifiers follow subgraph namespace conventions to prevent collisions.';
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Parse collision errors from a GraphQL error response.
 *
 * First checks for debug_hint in error extensions (from backend DebugHint enum),
 * then falls back to regex parsing of the error message.
 *
 * @param error - Apollo GraphQL error
 * @returns Array of parsed collision errors with suggestions
 */
export function parseCollisionErrors(error: ApolloError | Error | null): ParsedCollisionError[] {
  if (!error) return [];

  const errors: ParsedCollisionError[] = [];

  // Handle Apollo error with graphQLErrors
  if ('graphQLErrors' in error && error.graphQLErrors?.length > 0) {
    for (const gqlError of error.graphQLErrors) {
      // First try to extract debug_hint from extensions
      const extensions = gqlError.extensions as FerpErrorExtensions | undefined;
      const parsedError = parseFromExtensions(gqlError.message, extensions);
      if (parsedError) {
        errors.push(parsedError);
      } else {
        // Fall back to regex parsing of message
        errors.push(parseCollisionMessage(gqlError.message));
      }
    }
    return errors;
  }

  // Handle regular Error
  if (error.message) {
    errors.push(parseCollisionMessage(error.message));
  }

  return errors;
}

/**
 * Parse error from GraphQL extensions (debug_hint).
 *
 * If backend provides a DebugHint, use it for actionable suggestions.
 * Returns null if no useful hint is available.
 */
function parseFromExtensions(
  message: string,
  extensions?: FerpErrorExtensions
): ParsedCollisionError | null {
  if (!extensions) return null;

  const { debug_hint, suggestion, correlation_id, error_chain } = extensions;

  // If backend provided a suggestion, use it directly
  if (suggestion && debug_hint?.type !== 'None') {
    return {
      type: debugHintTypeToCollisionType(debug_hint?.type ?? 'Unknown'),
      message,
      identifier: extractIdentifierFromHint(debug_hint),
      suggestion,
      correlationId: correlation_id,
      errorChain: error_chain,
    };
  }

  // If no debug_hint or it's None, return null to fall back to regex parsing
  if (!debug_hint || debug_hint.type === 'None') {
    return null;
  }

  // Build suggestion from debug_hint
  return {
    type: debugHintTypeToCollisionType(debug_hint.type),
    message,
    identifier: extractIdentifierFromHint(debug_hint),
    firstSubgraph: debug_hint.subgraphs?.[0],
    secondSubgraph: debug_hint.subgraphs?.[1],
    suggestion: buildSuggestionFromHint(debug_hint),
    correlationId: correlation_id,
    errorChain: error_chain,
  };
}

/**
 * Convert DebugHintType to CollisionType for consistent handling.
 */
function debugHintTypeToCollisionType(hintType: DebugHintType | string): CollisionType {
  switch (hintType) {
    case 'YamlConfigInvalid':
      return 'YamlConfigInvalid';
    case 'RebuildRequired':
      return 'RebuildRequired';
    case 'ReseedRequired':
      return 'ReseedRequired';
    case 'CodegenStale':
      return 'CodegenStale';
    case 'ConfigNotFound':
      return 'ConfigNotFound';
    case 'SubgraphCollision':
      // SubgraphCollision has sub-types, fall back to Unknown
      return 'Unknown';
    case 'DatabaseConnectivity':
      return 'DatabaseConnectivity';
    default:
      return 'Unknown';
  }
}

/**
 * Extract the most relevant identifier from a debug hint.
 */
function extractIdentifierFromHint(hint?: DebugHint): string {
  if (!hint) return 'unknown';

  // Try various fields that might contain the identifier
  return (
    hint.config_id ??
    hint.config_type ??
    hint.file ??
    hint.identifier ??
    hint.var_name ??
    hint.service ??
    hint.database ??
    'unknown'
  );
}

/**
 * Build a human-readable suggestion from a debug hint.
 */
function buildSuggestionFromHint(hint: DebugHint): string {
  switch (hint.type) {
    case 'YamlConfigInvalid':
      if (hint.line) {
        return `Check YAML syntax at ${hint.file}:${hint.line}${hint.column ? `:${hint.column}` : ''}. ${hint.parse_error ?? ''}`;
      }
      return `Check YAML syntax in ${hint.file}. ${hint.parse_error ?? ''}`;

    case 'RebuildRequired':
      return `${hint.reason ?? 'Gateway needs rebuild'}. Run: ${hint.command ?? './scripts/ferp.sh deploy build'}`;

    case 'ReseedRequired':
      return `Config '${hint.config_type}' needs reseeding. Run: ${hint.command ?? `./scripts/ferp.sh seed ${hint.config_type ?? 'all'}`}`;

    case 'CodegenStale':
      return `GraphQL types are stale${hint.schema_changed ? ' (schema changed)' : ''}. Run: ${hint.command ?? 'pnpm run codegen'}`;

    case 'NetworkTimeout':
      return `Service '${hint.service}' timed out${hint.timeout_secs ? ` after ${hint.timeout_secs}s` : ''}. Check connectivity.`;

    case 'PermissionDenied':
      return `Missing required scopes: ${hint.required_scopes?.join(', ') ?? 'unknown'}`;

    case 'ConfigNotFound':
      return `Config '${hint.config_id}' not found. ${hint.expected_filename ? `Expected: ${hint.expected_filename}` : ''} ${hint.config_dir ? `in ${hint.config_dir}` : ''}`;

    case 'SubgraphCollision':
      return hint.suggestion ?? 'Resolve subgraph naming collision';

    case 'DatabaseConnectivity':
      if (hint.is_local_dev) {
        return `${hint.database ?? 'Database'} connection failed. Run: ${hint.check_command ?? './scripts/dev/start-local-full-stack.sh'}`;
      }
      return `${hint.database ?? 'Database'} connection failed. Check connectivity.`;

    case 'MissingEnvVar':
      if (hint.example_value) {
        return `Set environment variable: ${hint.var_name}=${hint.example_value}`;
      }
      return `Set environment variable: ${hint.var_name}`;

    case 'None':
    default:
      return 'No specific action suggested';
  }
}

/**
 * Check if an error is a collision error.
 *
 * @param error - Error to check
 * @returns True if the error is a collision error
 */
export function isCollisionError(error: ApolloError | Error | null): boolean {
  if (!error) return false;

  const parsed = parseCollisionErrors(error);
  return parsed.some(
    (e) =>
      e.type !== 'Unknown' &&
      (e.type.includes('Collision') || e.type === 'NamespaceValidation')
  );
}

/**
 * Display collision error notifications with suggestions.
 *
 * Uses Mantine notifications to show user-friendly messages
 * with actionable suggestions for resolving collisions.
 *
 * @param error - GraphQL error to display
 * @param context - Optional context for the error (e.g., "loading tenant config")
 */
export function showCollisionErrorNotifications(
  error: ApolloError | Error | null,
  context?: string
): void {
  const parsed = parseCollisionErrors(error);

  if (parsed.length === 0) {
    // Show generic error if no collision errors parsed
    notifications.show({
      title: 'Configuration Error',
      message: error?.message ?? 'An unexpected error occurred',
      color: 'red',
      autoClose: 10000,
    });
    return;
  }

  // Show notification for each collision error
  for (const collisionError of parsed) {
    const title = getNotificationTitle(collisionError.type, context);
    const message = buildNotificationMessage(collisionError);

    notifications.show({
      title,
      message,
      color: getNotificationColor(collisionError.type),
      autoClose: false, // Critical errors should not auto-close
    });
  }

  // Log to console for debugging/audit
  console.error('[FERP] Collision errors detected:', parsed);
}

/**
 * Display a single collision error notification with full details.
 *
 * @param collisionError - Parsed collision error
 */
export function showCollisionErrorDetail(collisionError: ParsedCollisionError): void {
  notifications.show({
    title: getNotificationTitle(collisionError.type),
    message: buildNotificationMessage(collisionError),
    color: getNotificationColor(collisionError.type),
    autoClose: false,
  });
}

// ============================================================================
// Notification Helpers
// ============================================================================

function getNotificationTitle(type: CollisionType, context?: string): string {
  const baseTitle: Record<CollisionType, string> = {
    RouteCollision: 'Route Conflict Detected',
    NavItemCollision: 'Navigation ID Conflict',
    EntityTypeCollision: 'Entity Type Conflict',
    PermissionScopeCollision: 'Permission Scope Conflict',
    NamespaceValidation: 'Namespace Convention Error',
    MultipleCollisions: 'Multiple Configuration Conflicts',
    // New actionable error types
    ReseedRequired: 'Configuration Needs Reseeding',
    RebuildRequired: 'Gateway Rebuild Required',
    CodegenStale: 'GraphQL Types Stale',
    YamlConfigInvalid: 'Invalid YAML Configuration',
    ConfigNotFound: 'Configuration Not Found',
    DatabaseConnectivity: 'Database Connection Error',
    Unknown: 'Configuration Error',
  };

  const title = baseTitle[type];
  return context ? `${title} (${context})` : title;
}

function getNotificationColor(type: CollisionType): string {
  switch (type) {
    case 'RouteCollision':
    case 'EntityTypeCollision':
    case 'PermissionScopeCollision':
      return 'red'; // Critical - affects functionality
    case 'NavItemCollision':
      return 'orange'; // Important - affects UI
    case 'NamespaceValidation':
      return 'yellow'; // Warning - preventable
    case 'MultipleCollisions':
      return 'red'; // Critical - multiple issues
    // New actionable error types
    case 'ReseedRequired':
    case 'RebuildRequired':
    case 'CodegenStale':
      return 'blue'; // Actionable - clear fix available
    case 'YamlConfigInvalid':
    case 'ConfigNotFound':
      return 'orange'; // Important - needs investigation
    case 'DatabaseConnectivity':
      return 'red'; // Critical - system issue
    default:
      return 'red';
  }
}

function buildNotificationMessage(error: ParsedCollisionError): string {
  const parts: string[] = [];

  // Main message (shortened for notification)
  if (error.identifier !== 'unknown' && error.identifier !== 'multiple') {
    parts.push(`Conflict on: "${error.identifier}"`);
  }

  // Involved subgraphs
  if (error.firstSubgraph && error.secondSubgraph) {
    parts.push(`Between: ${error.firstSubgraph} and ${error.secondSubgraph}`);
  }

  // Suggestion (most important)
  if (error.suggestion) {
    parts.push(`Fix: ${error.suggestion}`);
  }

  return parts.join('\n');
}

// ============================================================================
// React Hook for Collision Error Handling
// ============================================================================

/**
 * Hook options for collision error handling.
 */
export interface UseCollisionErrorHandlerOptions {
  /** Whether to automatically show notifications (default: true) */
  autoShowNotifications?: boolean;
  /** Context string for error messages */
  context?: string;
}

/**
 * Hook result for collision error handling.
 */
export interface UseCollisionErrorHandlerResult {
  /** Parsed collision errors */
  collisionErrors: ParsedCollisionError[];
  /** Whether any collision errors exist */
  hasCollisions: boolean;
  /** Manually show notifications */
  showNotifications: () => void;
  /** Clear collision errors */
  clearErrors: () => void;
}

/**
 * React hook for handling collision errors from GraphQL queries/mutations.
 *
 * Usage:
 * ```tsx
 * const { config, error } = useTenantSubgraphConfig(tenantId);
 * const { hasCollisions, collisionErrors } = useCollisionErrorHandler(error, {
 *   context: 'loading tenant config',
 *   autoShowNotifications: true,
 * });
 *
 * if (hasCollisions) {
 *   return <CollisionErrorPanel errors={collisionErrors} />;
 * }
 * ```
 */
import { useEffect, useState, useCallback, useRef } from 'react';

export function useCollisionErrorHandler(
  error: ApolloError | Error | null,
  options: UseCollisionErrorHandlerOptions = {}
): UseCollisionErrorHandlerResult {
  const { autoShowNotifications = true, context } = options;
  const [collisionErrors, setCollisionErrors] = useState<ParsedCollisionError[]>([]);
  const shownErrorsRef = useRef<Set<string>>(new Set());

  // Parse errors when error changes
  useEffect(() => {
    if (error) {
      const parsed = parseCollisionErrors(error);
      setCollisionErrors(parsed);

      // Auto-show notifications if enabled
      if (autoShowNotifications && parsed.length > 0) {
        // Only show notifications we haven't shown before
        const newErrors = parsed.filter((e) => {
          const key = `${e.type}-${e.identifier}`;
          if (shownErrorsRef.current.has(key)) return false;
          shownErrorsRef.current.add(key);
          return true;
        });

        if (newErrors.length > 0) {
          for (const collisionError of newErrors) {
            showCollisionErrorDetail(collisionError);
          }
        }
      }
    } else {
      setCollisionErrors([]);
    }
  }, [error, autoShowNotifications, context]);

  const showNotifications = useCallback(() => {
    if (error) {
      showCollisionErrorNotifications(error, context);
    }
  }, [error, context]);

  const clearErrors = useCallback(() => {
    setCollisionErrors([]);
    shownErrorsRef.current.clear();
  }, []);

  return {
    collisionErrors,
    hasCollisions: collisionErrors.length > 0,
    showNotifications,
    clearErrors,
  };
}

// ============================================================================
// Export Namespace Conventions for UI Components
// ============================================================================

export { NAMESPACE_CONVENTIONS };
